const generateId = () => typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);

export const emptyToNull = (value) => {
  if (value == null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  return value;
};

/** Matches production training_plans_status_check. */
const TRAINING_PLAN_STATUSES = new Set(['active', 'completed', 'paused', 'archived']);
const TRAINING_PLAN_STATUS_ALIASES = {
  draft: 'active',
  scheduled: 'active',
  inactive: 'paused',
};

export function normalizeTrainingPlanStatus(status) {
  const raw = typeof status === 'string' ? status.trim().toLowerCase() : '';
  if (TRAINING_PLAN_STATUSES.has(raw)) return raw;
  if (TRAINING_PLAN_STATUS_ALIASES[raw]) return TRAINING_PLAN_STATUS_ALIASES[raw];
  return 'active';
}

const intOrNull = (value) => {
  if (value == null || value === '') return null;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * Serializes the Zustand store into the canonical JSON target schema.
 * User-empty fields are `null` (not "" or invented defaults).
 */
export function serializePlan(storeState) {
  const { meta, workoutOrder, workouts, exercises, sets, weekOrder, weeks } = storeState;

  const plan_structure = {
    template_id: emptyToNull(meta.template_id),
    name: emptyToNull(meta.name),
    duration_weeks: intOrNull(meta.duration_weeks),
    is_repeating: meta.is_repeating ?? null,
    intensity_level: emptyToNull(meta.intensity_level),
    focus: emptyToNull(meta.focus),
    pre_workout_notes: emptyToNull(meta.pre_workout_notes),
    weeks: (weekOrder || []).map((wId, index) => {
      const week = weeks[wId] || {};
      return {
        week_id: emptyToNull(week.week_id) || `week_${generateId()}`,
        week_number: index + 1,
        focus: emptyToNull(week.focus),
        days: (week.slots || []).map((slot, dayIndex) => ({
          slot_id: emptyToNull(slot.slot_id) || `slot_${generateId()}`,
          day_number: dayIndex + 1,
          kind: emptyToNull(slot.kind),
          workout_id: emptyToNull(slot.workout_id),
          notes: emptyToNull(slot.notes)
        }))
      };
    }),
    workouts: (workoutOrder || []).map((wId, wIndex) => {
      const w = workouts[wId] || {};
      return {
        workout_id: emptyToNull(w.workout_id) || `wk_${generateId()}`,
        day_name: emptyToNull(w.day_name),
        recommended_day: emptyToNull(w.recommended_day),
        order: wIndex + 1,
        notes: emptyToNull(w.notes),
        exercises: (w.exerciseIds || []).map((eId, eIndex) => {
          const e = exercises[eId] || {};
          return {
            exercise_instance_id: emptyToNull(e.exercise_instance_id) || `ex_${generateId()}`,
            global_exercise_id: emptyToNull(e.global_exercise_id),
            exercise_name: emptyToNull(e.exercise_name),
            order: eIndex + 1,
            rest_seconds: intOrNull(e.rest_seconds),
            coach_notes: emptyToNull(e.coach_notes),
            sets: (e.setIds || []).map((sId, sIndex) => {
              const s = sets[sId] || {};
              return {
                set_id: emptyToNull(s.set_id) || `set_${generateId()}`,
                set_index: sIndex + 1,
                set_type: emptyToNull(s.set_type),
                min_reps: intOrNull(s.min_reps),
                max_reps: intOrNull(s.max_reps),
                target_rpe: intOrNull(s.target_rpe),
                target_weight: emptyToNull(s.target_weight)
              };
            })
          };
        })
      };
    })
  };

  return plan_structure;
}

/**
 * Hydrates the database row into the Zustand store shape.
 * Handles one-way migration of legacy `weeks` into `workouts`.
 */
export function hydratePlan(dbRow) {
  const storeData = {
    meta: {
      template_id: `tpl_${generateId()}`,
      name: dbRow.plan_name || '',
      duration_weeks: dbRow.duration_weeks || 1,
      is_repeating: true,
      intensity_level: 'moderate-high',
      focus: dbRow.goal || '',
      pre_workout_notes: ''
    },
    assignment: {
      user_code: dbRow.user_code || '',
      status: normalizeTrainingPlanStatus(dbRow.status),
      active_from: dbRow.active_from || new Date().toISOString().split('T')[0],
      active_until: dbRow.active_until || '',
      goal: dbRow.goal || 'strength_training',
      difficulty_level: dbRow.difficulty_level || 'beginner',
      notes: dbRow.notes || ''
    },
    workoutOrder: [],
    workouts: {},
    weekOrder: [],
    weeks: {},
    exercises: {},
    sets: {},
    ui: {
      expandedWorkoutId: null,
      expandedExerciseId: null,
      searchOpenForWorkoutId: null,
      editingWorkoutId: null
    }
  };

  const ps = dbRow.plan_structure || {};

  storeData.meta.template_id = ps.template_id || storeData.meta.template_id;
  storeData.meta.name = ps.name || storeData.meta.name;
  storeData.meta.duration_weeks = ps.duration_weeks || storeData.meta.duration_weeks;
  storeData.meta.is_repeating = ps.is_repeating ?? true;
  storeData.meta.intensity_level = ps.intensity_level || storeData.meta.intensity_level;
  storeData.meta.focus = ps.focus || storeData.meta.focus;
  storeData.meta.pre_workout_notes = ps.pre_workout_notes || '';

  // 1. Process all workouts into flat stores
  if (ps.workouts && Array.isArray(ps.workouts)) {
    ps.workouts.forEach(w => {
      const wId = w.workout_id || generateId();
      storeData.workoutOrder.push(wId);
      storeData.workouts[wId] = {
        workout_id: wId,
        day_name: w.day_name || '',
        recommended_day: w.recommended_day || 'monday',
        order: w.order || 1,
        exerciseIds: []
      };

      (w.exercises || []).forEach((e, eIdx) => {
        const eId = e.exercise_instance_id || generateId();
        storeData.workouts[wId].exerciseIds.push(eId);
        
        // Handle legacy single-value reps/rest
        let min_reps = 8;
        let max_reps = 10;
        if (typeof e.reps === 'string') {
          const parts = e.reps.split('-');
          if (parts.length === 2) {
            min_reps = parseInt(parts[0], 10) || 8;
            max_reps = parseInt(parts[1], 10) || 10;
          } else if (parts.length === 1) {
            min_reps = max_reps = parseInt(parts[0], 10) || 8;
          }
        } else if (typeof e.reps === 'number') {
          min_reps = max_reps = e.reps;
        }

        storeData.exercises[eId] = {
          exercise_instance_id: eId,
          global_exercise_id: e.global_exercise_id || e.exercise_id || null,
          exercise_name: e.exercise_name || '',
          order: e.order || eIdx + 1,
          rest_seconds: e.rest_seconds || 90,
          coach_notes: e.coach_notes || e.notes || '',
          setIds: []
        };

        const setsArray = e.sets || [];
        if (setsArray.length > 0 && typeof setsArray[0] === 'object') {
          setsArray.forEach(s => {
            const sId = s.set_id || generateId();
            storeData.exercises[eId].setIds.push(sId);
            storeData.sets[sId] = {
              set_id: sId,
              set_index: s.set_index || 1,
              set_type: s.set_type || 'working',
              min_reps: s.min_reps || 8,
              max_reps: s.max_reps || 10,
              target_rpe: s.target_rpe || 5,
              target_weight: s.target_weight || ''
            };
          });
        } else {
          // Legacy inline sets number
          const numSets = parseInt(e.sets, 10) || 3;
          for (let i = 0; i < numSets; i++) {
            const sId = generateId();
            storeData.exercises[eId].setIds.push(sId);
            storeData.sets[sId] = {
              set_id: sId,
              set_index: i + 1,
              set_type: 'working',
              min_reps,
              max_reps,
              target_rpe: 5,
              target_weight: e.target_weight_kg ? String(e.target_weight_kg) : (e.target_weight || '')
            };
          }
        }
      });
    });
  } 

  // 2. Process Weeks (Grid layout)
  if (ps.weeks && Array.isArray(ps.weeks) && ps.weeks.length > 0) {
    ps.weeks.forEach((w, wIdx) => {
      const weekId = w.week_id || generateId();
      storeData.weekOrder.push(weekId);
      const newWeek = {
        week_id: weekId,
        week_number: w.week_number || wIdx + 1,
        focus: w.focus || '',
        slots: []
      };

      const dbDays = w.days || [];
      for (let i = 0; i < 7; i++) {
        const d = dbDays[i] || {};
        let kind = d.kind || 'rest';
        let workout_id = d.workout_id || null;
        
        // Map index to day names for fallback day name
        const englishDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const fallbackDayName = englishDays[i];

        // Legacy migration: if it has inline exercises, we create a workout for it
        if (d.exercises && Array.isArray(d.exercises) && d.exercises.length > 0) {
          kind = 'workout';
          workout_id = generateId();
          storeData.workoutOrder.push(workout_id);
          storeData.workouts[workout_id] = {
            workout_id,
            day_name: d.day_name || fallbackDayName,
            recommended_day: 'monday',
            order: storeData.workoutOrder.length,
            exerciseIds: []
          };
          
          d.exercises.forEach((e, eIdx) => {
            const eId = generateId();
            storeData.workouts[workout_id].exerciseIds.push(eId);
            
            let min_reps = 8, max_reps = 10;
            if (typeof e.reps === 'string') {
              const parts = e.reps.split('-');
              if (parts.length === 2) { min_reps = parseInt(parts[0],10)||8; max_reps = parseInt(parts[1],10)||10; }
              else { min_reps = max_reps = parseInt(parts[0],10)||8; }
            } else if (typeof e.reps === 'number') { min_reps = max_reps = e.reps; }
            
            storeData.exercises[eId] = {
              exercise_instance_id: eId,
              global_exercise_id: e.exercise_id || null,
              exercise_name: e.exercise_name || '',
              order: e.order || eIdx + 1,
              rest_seconds: parseInt(e.rest_seconds, 10) || 90,
              coach_notes: e.notes || '',
              setIds: []
            };

            const numSets = parseInt(e.sets, 10) || 3;
            for (let sIdx = 0; sIdx < numSets; sIdx++) {
              const sId = generateId();
              storeData.exercises[eId].setIds.push(sId);
              storeData.sets[sId] = {
                set_id: sId,
                set_index: sIdx + 1,
                set_type: 'working',
                min_reps, max_reps, target_rpe: 5,
                target_weight: e.target_weight_kg ? String(e.target_weight_kg) : (e.target_weight || '')
              };
            }
          });
        } else if (d.day_name && !kind && !workout_id && d.exercises && d.exercises.length === 0) {
           // Empty legacy day
           kind = 'rest';
        }

        newWeek.slots.push({
          slot_id: d.slot_id || generateId(),
          kind,
          workout_id,
          notes: d.notes || ''
        });
      }
      storeData.weeks[weekId] = newWeek;
    });
  } 
  // 3. Fallback: If we had Flat Workouts but NO Weeks (Accordion flat format)
  else if (storeData.workoutOrder.length > 0) {
    const weekId = generateId();
    storeData.weekOrder.push(weekId);
    const slots = Array.from({ length: 7 }).map(() => ({ slot_id: generateId(), kind: 'rest', workout_id: null, notes: '' }));
    
    // Assign workouts sequentially to slots
    storeData.workoutOrder.forEach((wId, i) => {
      if (i < 7) {
        slots[i].kind = 'workout';
        slots[i].workout_id = wId;
      }
    });

    storeData.weeks[weekId] = {
      week_id: weekId,
      week_number: 1,
      focus: '',
      slots
    };
  }
  // 4. Empty State
  else {
    const weekId = generateId();
    storeData.weekOrder.push(weekId);
    storeData.weeks[weekId] = {
      week_id: weekId,
      week_number: 1,
      focus: '',
      slots: Array.from({ length: 7 }).map(() => ({ slot_id: generateId(), kind: 'rest', workout_id: null, notes: '' }))
    };
  }

  return storeData;
}

const WEEKDAY_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/**
 * Resolve a calendar day from plan_structure for display.
 * Rest is `kind === 'rest'` (or legacy days with no workout / no exercises).
 * Workout days join `day.workout_id` to `plan_structure.workouts`.
 */
export function getPlanDayView(day, workouts = [], language = 'english') {
  const index = Math.max(0, Number(day?.day_number || 1) - 1) % 7;
  const weekday = language === 'hebrew' ? WEEKDAY_HE[index] : WEEKDAY_EN[index];
  const workout = day?.workout_id
    ? (workouts || []).find((w) => w.workout_id === day.workout_id) || null
    : null;
  const legacyExercises = Array.isArray(day?.exercises) ? day.exercises : [];
  const isRest =
    day?.kind === 'rest' ||
    (!day?.kind && !workout && legacyExercises.length === 0);

  const exercises = workout?.exercises || legacyExercises;
  const title = isRest
    ? (language === 'hebrew' ? 'יום מנוחה' : 'Rest day')
    : (workout?.day_name || day?.day_name || weekday);

  return { isRest, workout, exercises, weekday, title };
}

export function formatExerciseSetsSummary(ex) {
  if (Array.isArray(ex?.sets)) {
    const first = ex.sets[0];
    const min = first?.min_reps;
    const max = first?.max_reps;
    const reps =
      min != null && max != null && min !== max
        ? `${min}–${max}`
        : String(max ?? min ?? '—');
    return {
      setCount: ex.sets.length,
      reps,
      rest: ex.rest_seconds ?? ex.rest ?? '—',
      notes: ex.coach_notes || ex.notes || '',
    };
  }
  return {
    setCount: ex?.sets ?? '—',
    reps: ex?.reps ?? '—',
    rest: ex?.rest_seconds ?? ex?.rest ?? '—',
    notes: ex?.notes || '',
  };
}
